import { FC, useEffect } from "react";
import { Button, ButtonVariant, Hide } from "shared/components";
import WarningFillIcon from "../../../icons/warningFillIcon.svg?react";
import { Checkbox } from "shared/components/forms/Checkbox";
import { useTemplateContext } from "../provider";
import { ApplicantType } from "shared/types";
import { FeesComponent } from "../slices/VerificationsSlice/components/FeesComponent";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import templatesApi from "../../../../api/landlord/templates";
import { VerificationSchemaView } from "../slices/types";
import { screeningServicesTabs } from "../constant";
import { CheckComponent } from "./CheckComponent";
import { ApplicantRoleLabel } from "shared/utils";

interface Props {
  onCancel: () => void;
  onSave?: () => void;
}

export const LayoutFooter: FC<Props> = ({ onCancel, onSave }) => {
  const {
    template,
    setTemplate,
    partnerType,
    readOnly,
    selectedVerificationSchema,
    setSelectedVerificationSchema
  } = useTemplateContext();
  const userPaid = Boolean(template?.[`${partnerType}_paid`]);

  const { canSave } = useTemplateContext();

  const handleApplicantPaidChange = (v: boolean) => {
    if (!template) return;
    setTemplate({
      ...template,
      [`${partnerType}_paid`]: v,
      [`${partnerType}_fee`]: {
        ...template[`${partnerType}_fee`],
        cents: v ? template[`${partnerType}_fee`].cents || 6000 : 0
      }
    });
  };

  const hasBackButton =
    selectedVerificationSchema === VerificationSchemaView.CreditReport ||
    screeningServicesTabs.includes(selectedVerificationSchema);

  const [debouncedTemplateParams] = useDebouncedValue(template, 500);

  const updateApplicationFeeCents = (newCents: number) => {
    if (!template) return;

    const updatedTemplate = {
      ...template,
      [`${partnerType}_fee`]: {
        ...template[`${partnerType}_fee`],
        cents: newCents
      }
    };
    setTemplate(updatedTemplate);
  };

  const { data: feeData, refetch } = useQuery({
    queryKey: [
      "fetchFeeData",
      debouncedTemplateParams?.applicant_fee,
      debouncedTemplateParams?.co_signer_fee
    ],
    queryFn: () => {
      if (!debouncedTemplateParams) return Promise.resolve(null);
      return templatesApi.validateTemplate(debouncedTemplateParams);
    },
    placeholderData: keepPreviousData,
    enabled: Boolean(debouncedTemplateParams)
  });

  useEffect(() => {
    refetch();
  }, [debouncedTemplateParams]);

  return (
    <>
      <Hide
        if={
          [ApplicantType.CoSigner, ApplicantType.Occupant].includes(
            partnerType
          ) && !template?.settings[partnerType]?.enabled_screening_services
        }
      >
        {template &&
        selectedVerificationSchema ===
          VerificationSchemaView.ScreeningServices ? (
          <div className="px-6 py-4 border-t border-alpha-100">
            <div className="pb-2">
              {readOnly ? (
                <CheckComponent
                  checked={userPaid}
                  label={`${ApplicantRoleLabel[partnerType]} paid`}
                />
              ) : (
                <Checkbox
                  checked={userPaid}
                  label={`${ApplicantRoleLabel[partnerType]} paid`}
                  onChange={(e) => handleApplicantPaidChange(e.target.checked)}
                />
              )}
            </div>
            <FeesComponent
              feeData={
                feeData?.verification_template.costs?.[partnerType] ||
                // TODO: this is visual hotfix, need to be removed after backend fix
                template.costs?.[partnerType]
              }
              onFeeChange={(newCents) => updateApplicationFeeCents(newCents)}
              readOnly={readOnly}
              isApplicantPaid={userPaid}
            />
          </div>
        ) : null}
      </Hide>
      <Hide if={readOnly}>
        <div className="flex items-center justify-between md:gap-4 gap-2 flex-wrap md:flex-nowrap py-4 px-6 border-t border-alpha-100">
          <div className="flex items-center gap-2.5">
            <WarningFillIcon className="text-content-400 size-5" />

            <p className="text-sm font-medium text-content-400">
              Changes will only affect new applications
            </p>
          </div>

          <div className="flex items-center gap-2">
            {hasBackButton ? (
              <Button
                variant={ButtonVariant.BASE}
                onClick={() =>
                  setSelectedVerificationSchema(
                    VerificationSchemaView.ScreeningServices
                  )
                }
                className="w-[125px]"
              >
                Back
              </Button>
            ) : (
              <Button
                variant={ButtonVariant.BASE}
                onClick={onCancel}
                className="w-[125px]"
              >
                Cancel
              </Button>
            )}
            <Button
              variant={ButtonVariant.PRIMARY}
              onClick={() => {
                canSave && onSave?.();
              }}
              className="w-[125px]"
              disabled={!canSave}
            >
              Save changes
            </Button>
          </div>
        </div>
      </Hide>
    </>
  );
};
